<template>
  <v-row>
    <v-col v-for="item in filteredMeals" :key="item.id" cols="12" sm="6" md="4" lg="3">
      <v-card class="meal-card standard-card-override">
        <!-- Image Area with Actions -->
        <div class="image-area">
          <v-img
            v-if="hasValidImage(item)"
            :src="getMealImageUrl(item)"
            height="220px"
            class="meal-image"
            gradient="to bottom, rgba(38,50,56,.15), rgba(38,50,56,.55)"
          >
            <!-- Title Overlay -->
            <div class="image-title-overlay">
              <div class="image-title">{{ item.name || $t("no_name") }}</div>
            </div>
          </v-img>
          <div v-else class="no-image-placeholder">
            <v-icon
              color="blue-grey lighten-2"
              size="64"
              class="meal-image-placeholder-icon"
            >
              mdi-food-variant
            </v-icon>
            <div class="placeholder-title">{{ item.name || $t("no_name") }}</div>
          </div>

          <!-- Edit and Delete buttons in top-right -->
          <div class="action-btn-group">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="$parent.editMeal(item)"
                  color="white"
                  class="action-btn"
                >
                  <v-icon small color="green darken-1">mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("edit_meal") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="$parent.deleteMeal(item)"
                  color="white"
                  class="action-btn"
                >
                  <v-icon small color="red darken-1">mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("delete_meal") }}</span>
            </v-tooltip>
          </div>

          <!-- Floating action buttons -->
          <div class="floating-image-btn right">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  x-small
                  color="white"
                  class="elevation-3 mr-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="showQr(item)"
                >
                  <v-icon small color="blue darken-1">mdi-qrcode</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("tooltip_show_meal_qr") }}</span>
            </v-tooltip>

            <print-button @click="print(item)"></print-button>
          </div>
        </div>

        <!-- Card Content -->
        <v-card-text class="card-content">
          <!-- Category -->
          <div v-if="item.category" class="meal-category mb-3">
            <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
              {{ $t("group") }}
            </div>
            <v-chip small label color="primary" class="mr-1 mb-1">
              {{ translate(item.category.name) }}
            </v-chip>
          </div>

          <!-- Menu Types -->
          <div
            v-if="item.menu_types && item.menu_types.length"
            class="meal-menu-types mb-3"
          >
            <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
              {{ $t("menu_type") }}
            </div>
            <v-chip
              v-for="menuType in item.menu_types"
              :key="menuType.id"
              small
              label
              color="secondary"
              class="mr-1 mb-1"
            >
              {{ $t(menuType.group) || menuType.group }}
            </v-chip>
          </div>

          <!-- Price -->
          <div v-if="item.price" class="meal-price mb-3 text-right">
            <div class="text-caption grey--text text--darken-1">{{ $t("price") }}</div>
            <div class="text-h6 font-weight-bold primary--text">
              {{ item.price }}{{ restaurant.settings?.price || "" }}
            </div>
          </div>

          <!-- Groceries List -->
          <div v-if="(item.groceries || []).length" class="meal-foods mb-3">
            <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
              {{ $t("groceri") }} ({{ item.groceries.length }})
            </div>
            <div class="foods-list">
              <div v-for="grocery in item.groceries" :key="grocery.id" class="food-item">
                <v-icon x-small color="grey" class="mr-1">mdi-circle-small</v-icon>
                <span class="text-body-2">
                  {{ grocery.name }}: {{ grocery.pivot?.quantity }} {{ grocery.measure }}
                </span>
              </div>
            </div>
          </div>

          <!-- Daily Offer Checkbox -->
          <v-checkbox
            v-model="item.for_sale"
            :label="$t('s_v')"
            hide-details
            dense
            color="primary"
            class="mt-2"
            @change="forSale(item)"
          ></v-checkbox>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import mealActions from "@/mixins/mealActions";
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "MealsCardsView",
  mixins: [mealActions, hasPrinting, hasSnackbar],
  props: {
    meals: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: "",
    },
  },
  data: () => ({}),
  computed: {
    restaurant() {
      return this.$store.state.restaurant;
    },
    filteredMeals() {
      if (!this.search || this.search.trim() === "") {
        return this.meals;
      }

      const searchLower = this.search.toLowerCase();
      return this.meals.filter((meal) => {
        // Search in meal name
        if (meal.name && meal.name.toLowerCase().includes(searchLower)) {
          return true;
        }

        // Search in category name
        if (
          meal.category &&
          meal.category.name &&
          this.translate(meal.category.name).toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        // Search in price
        if (meal.price && meal.price.toString().includes(searchLower)) {
          return true;
        }

        // Search in groceries
        if (meal.groceries && meal.groceries.length) {
          const foundInGroceries = meal.groceries.some(
            (grocery) =>
              grocery.name && grocery.name.toLowerCase().includes(searchLower)
          );
          if (foundInGroceries) return true;
        }

        // Search in menu types
        if (meal.menu_types && meal.menu_types.length) {
          const foundInMenuTypes = meal.menu_types.some(
            (menuType) =>
              menuType.group &&
              (menuType.group.toLowerCase().includes(searchLower) ||
                this.$t(menuType.group).toLowerCase().includes(searchLower))
          );
          if (foundInMenuTypes) return true;
        }

        return false;
      });
    },
  },
  methods: {
    translate(value) {
      if (typeof value === "string" || value instanceof String) {
        return this.$t(value) ? this.$t(value) : value;
      }
      return value;
    },
    hasValidImage(item) {
      const imageUrl = this.getMealImageUrl(item);
      return (
        imageUrl &&
        imageUrl !== "./media/img/no-image.webp" &&
        imageUrl !== "/img/no-image.png"
      );
    },
    getMealImageUrl(item) {
      if (!item || !item.image || item.image.name === "no-image.png") {
        return null;
      }
      return this.galleryUrl(item.image.name);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_variables.scss";

// Card styling
.meal-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s;
  overflow: visible;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.meal-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

// Image area
.image-area {
  position: relative;
  height: 220px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background: linear-gradient(135deg, #eceff1 60%, #b0bec5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.meal-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

// No image placeholder
.no-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}

.meal-image-placeholder-icon {
  margin-bottom: 16px;
}

.placeholder-title {
  color: #546e7a;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 8px;
}

// Image title overlay
.image-title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
}

.image-title {
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

// Action buttons in top-right
.action-btn-group {
  position: absolute;
  top: 10px;
  right: 12px;
  display: flex;
  flex-direction: row;
  gap: 6px;
  z-index: 2;
}

.action-btn {
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 2px 8px rgba(38, 50, 56, 0.08);
  border-radius: 50%;
  transition: background 0.2s, box-shadow 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 1) !important;
    box-shadow: 0 4px 16px rgba(38, 50, 56, 0.16);
  }
}

// Floating action buttons
.floating-image-btn {
  position: absolute;
  z-index: 0;
  pointer-events: auto;
  display: flex;
  gap: 6px;

  .v-btn {
    border: 2px solid #e3eaf3;
    background: #fff !important;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 4px 16px rgba(33, 150, 243, 0.18);
    }
  }
}

.floating-image-btn.right {
  top: 200px;
  right: 18px;
}

// Card content
.card-content {
  padding: 16px 24px;
  flex: 1;
}

// Meal sections styling
.meal-category,
.meal-info,
.meal-foods,
.meal-status {
  padding: 0;
}

// Foods list compact display
.foods-list {
  max-height: 150px;
  overflow-y: auto;

  .food-item {
    display: flex;
    align-items: center;
    padding: 4px 0;

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }
}

// Scrollbar styling for foods list
.foods-list::-webkit-scrollbar {
  width: 4px;
}

.foods-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.foods-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>
